import { useEffect, useState } from "react";
import { PlusIcon } from "lucide-react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { DeleteUserDialog } from "@/components/tools/DeleteUserDialog";
import { UserFormDialog } from "@/components/tools/UserFormDialog";
import { UsersTable } from "@/components/tools/UsersTable";
import { Button } from "@/components/ui/button";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetUsers } from "@/hooks/queries/useGetUsers";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { User } from "@/types";

export const ToolsUsersListPage = () => {
  /* Database CRUD Operations */
  // Read
  const { data: users, status } = useGetUsers();

  /* React Hooks */
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  useSetTitle({ title: "Tools | Users" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.USERS, {
      subScreenName: MIXPANEL_SUBSCREENS.LIST,
    });
  }, []);

  /* Handlers */
  const handleCreate = () => {
    mixpanelUtils.trackButtonClick("Add user", MIXPANEL_SCREENS.USERS);
    setSelectedUser(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setIsDeleteOpen(true);
  };

  /* Render */
  const render = () => {
    if (status === "pending") return <LoadingIndicator variant="medium" />;
    if (status === "error")
      return <ErrorText errorMessage="Error loading users." />;
    return (
      <UsersTable
        onCreate={handleCreate}
        onDelete={handleDelete}
        onEdit={handleEdit}
        users={users}
      />
    );
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Users</TopbarTitle>
        <Button className="rounded-full" onClick={handleCreate}>
          <PlusIcon className="h-4 w-4" />
          Add user
        </Button>
      </TopbarContainer>
      <Column className="flex-1 overflow-hidden p-6">{render()}</Column>
      <UserFormDialog
        isOpen={isFormOpen}
        setIsOpen={setIsFormOpen}
        user={selectedUser}
      />
      <DeleteUserDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        user={userToDelete}
      />
    </Column>
  );
};
